import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Badge } from 'react-bootstrap';
import { debounce } from 'lodash';
import { shortenandHoverString } from '../../../../../Components/helper';
import MyTodoFilter from './MyTodoFilter';
import type { MyTodoFilterState } from './MyTodoFilter';
import type { RootState } from '../store/my-todo.types';

type UnderlineStyle = { width?: number; left?: number; display?: string };

const useTabUnderline = (activeTab: string, dependency: unknown) => {
  const tabRef = useRef<HTMLUListElement>(null);
  const [underlineStyle, setUnderlineStyle] = useState<UnderlineStyle>({});

  const calculatePosition = useCallback((): UnderlineStyle | null => {
    if (!tabRef.current) return null;

    const activeElement = tabRef.current.querySelector<HTMLLIElement>('.active');
    if (!activeElement || activeElement.offsetWidth === 0 || activeElement.offsetHeight === 0) {
      return { width: 0, display: 'none' };
    }

    const tabRect = activeElement.getBoundingClientRect();
    const parentRect = tabRef.current.getBoundingClientRect();

    if (tabRect.width > 0 && parentRect.width > 0) {
      return { width: tabRect.width, left: tabRect.left - parentRect.left };
    }

    return null;
  }, []);

  const updatePosition = useCallback(
    (useRAF = true) => {
      const updateFn = () => {
        const position = calculatePosition();
        if (position) {
          setUnderlineStyle(position);
        }
      };
      if (useRAF) {
        requestAnimationFrame(updateFn);
      } else {
        updateFn();
      }
    },
    [calculatePosition],
  );

  useEffect(() => {
    const timers = [50, 100, 200, 500].map((delay) => setTimeout(() => updatePosition(), delay));
    return () => timers.forEach(clearTimeout);
  }, [updatePosition, dependency]);

  useEffect(() => {
    const timer = setTimeout(() => updatePosition(), 100);
    const debouncedUpdate = debounce(() => updatePosition(), 300);
    const initialResizeTimer = setTimeout(() => updatePosition(false), 150);

    window.addEventListener('resize', debouncedUpdate);

    return () => {
      clearTimeout(timer);
      clearTimeout(initialResizeTimer);
      window.removeEventListener('resize', debouncedUpdate);
      debouncedUpdate.cancel();
    };
  }, [activeTab, updatePosition, dependency]);

  return { tabRef, underlineStyle };
};

interface MyTodoHeaderProps {
  activeTab: 'my-todo' | 'all-todo';
  onTabChange: (tab: 'my-todo' | 'all-todo') => void;
  searchText: string;
  onSearchChange: (value: string) => void;
  pendingCount: number;
  allTodoPendingCount: number;
  loading: boolean;
  filterState: MyTodoFilterState;
  setFilterState: React.Dispatch<React.SetStateAction<MyTodoFilterState>>;
  onApplyFilter: (filterObj: { status?: string[] }) => void;
}

const TAB_LIST = [
  { id: 'my-todo' as const, labelKey: 'MY_TODO.TABS.MY_TODO', countKey: 'pendingCount' },
  { id: 'all-todo' as const, labelKey: 'MY_TODO.TABS.ALL_TODO', countKey: 'allTodoPendingCount' },
];

function shouldShowAllTodoTab(role: string | undefined, isSupplierUserLoggedIn: boolean | undefined): boolean {
  if (role === 'User' || role === 'SupplierUser') {
    return false;
  }
  return !isSupplierUserLoggedIn;
}

const MyTodoHeader: React.FC<MyTodoHeaderProps> = ({
  activeTab,
  onTabChange,
  searchText,
  onSearchChange,
  pendingCount,
  allTodoPendingCount,
  loading,
  filterState,
  setFilterState,
  onApplyFilter,
}) => {
  const { t } = useTranslation('modules/my-todo');
  const { userInfo, isSupplierUserLoggedIn } = useSelector((state: RootState) => state.UserDetails);
  const visibleTabs = useMemo(() => {
    if (shouldShowAllTodoTab(userInfo?.role, isSupplierUserLoggedIn)) {
      return TAB_LIST;
    }
    return [];
  }, [userInfo?.role, isSupplierUserLoggedIn]);
  const expandSearchRef = useRef<HTMLDivElement>(null);
  const countDependency = `${pendingCount}-${allTodoPendingCount}`;
  const { tabRef: switchTabRef, underlineStyle: activeUnderline } = useTabUnderline(activeTab, countDependency);

  const handleIconClick = () => {
    expandSearchRef.current?.classList.toggle('active-input');
  };

  const handleClearSearch = () => {
    onSearchChange('');
  };

  const getCount = (key: string) => {
    if (key === 'pendingCount') return pendingCount;
    if (key === 'allTodoPendingCount') return allTodoPendingCount;
    return 0;
  };

  return (
    <>
      {/* Title bar — always visible */}
      <div className="page-title-filter sticky">
        <div className="title-container d-flex align-items-center flex-wrap">
          <h4 className="title d-inline-flex bom-title-listmb-0">{shortenandHoverString(t('MY_TODO.TITLE'), 30, 'bottom')}</h4>
        </div>
        <div className="ml-auto search-filter">
          <div className="action-icons-container">
            <div className="search-filter ml-8 action-new-row cp-search">
              <div className="search-filter">
                <div ref={expandSearchRef} className="common-expand-search without-dropdown">
                  <button type="button" onClick={handleIconClick} className="btn btn-toggle" aria-label={t('MY_TODO.SEARCH.ARIA_LABEL')}>
                    <span className="icon-Magnifying-Glass-Solid" />
                  </button>
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => onSearchChange(e.target.value)}
                    disabled={loading}
                    placeholder={t('MY_TODO.SEARCH.PLACEHOLDER')}
                  />
                  {searchText && (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="btn btn-clear-search"
                      aria-label={t('MY_TODO.SEARCH.CLEAR')}
                      title={t('MY_TODO.SEARCH.CLEAR')}
                    >
                      <span className="icon-Close" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="product-bom ml-8">
              <MyTodoFilter applyFilter={onApplyFilter} filterState={filterState} setFilterState={setFilterState} />
            </div>
          </div>
        </div>
      </div>
      <div className={`${visibleTabs?.length > 0 ? 'mt-16' : 'mt-24'}`}>
        {visibleTabs?.length > 0 && (
          <div className="container-fluid my-todo-tab-row">
            <div className="d-flex mb-16">
              <div className="common-switch-container">
                <ul className="new-switch my-todo-switch" ref={switchTabRef}>
                  {visibleTabs.map((tab) => {
                    const count = getCount(tab.countKey);
                    const isActive = activeTab === tab.id;
                    return (
                      <li key={tab.id} className={isActive ? 'active' : ''}>
                        <button type="button" disabled={loading} onClick={() => onTabChange(tab.id)} className="switch-item">
                          {t(tab.labelKey)}
                          {count > 0 ? <Badge className="my-todo-tab-count-badge ml-1">{count}</Badge> : <></>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <div className="tab-underline" style={activeUnderline} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MyTodoHeader;
